import { useEffect, useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from 'react-query';

import { selectLoggedInUserState } from '@store';
import { boardsServices, usersServices } from '@services';
import { getCleanedUrlParams, showError } from '@utils';
import {
  LIMIT,
  ENTITY_TYPES_COMMENTS,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
} from '@constants';

import { QUERY_KEYS } from 'constants/queryKeys';
import {
  useAssignees,
  useInfinitePagination,
  useServicePoints,
} from 'hooks/common';
import { useHistoryItemsChangelog } from 'hooks/historyItems';
import {
  getLeasingBuyoutsPayload,
  getSellRequestsPayload,
  handleUser,
  handleUserBookings,
  handleUserCreditScore,
  handleUserLeasingBuyouts,
  handleUserSubscriptions,
  handleUserWithSubs,
  handleOrders,
  handleInfiniteQueryUserData,
  handleUserComments,
  handleUserSMS,
} from './utils';

import { USER_HISTORY_CHANGES_DEFAULT_PAGE_SIZE } from './constants';

export const USER_ORDERS_B2B_PAGE_SIZE = LIMIT;
export const USER_BOOKINGS_PAGE_SIZE = LIMIT;

export const useUserOperations = () => {
  const router = useRouter();

  const loggedInUser = useSelector(selectLoggedInUserState);

  const { servicePointsMap, isLoading: isServicePointsLoading } =
    useServicePoints();
  const { getAssigneeName } = useAssignees();
  const { isSystemActor, handleChangelogItemFieldName } =
    useHistoryItemsChangelog();

  const pageSize = router.query?.pageSize;
  const b2bPageSize = parseInt(pageSize, 10) || USER_ORDERS_B2B_PAGE_SIZE;
  const bookingsPageSize = parseInt(pageSize, 10) || USER_BOOKINGS_PAGE_SIZE;
  const page = router.query?.page || 1;
  const userId = router.query?.userId;

  const historyTab = router.query?.historyTab;

  const isHistoryTabComment = historyTab === 'comment';
  const isHistoryTabSMS = historyTab === 'sms';

  const order = 'desc';
  const orderBy = 'createdAt';

  const {
    data: ordersResponse,
    isLoading: isUserOrdersLoading,
    refetch: refetchUserOrders,
  } = useQuery(
    [
      QUERY_KEYS.GET_USERS_B2B_ORDERS_KEY,
      { pageSize: b2bPageSize, order, orderBy, page },
    ],
    () =>
      usersServices.getUserOrders(
        userId,
        getCleanedUrlParams({
          page,
          pageSize: b2bPageSize,
          order,
          orderBy,
        })
      ),
    { enabled: !!userId, refetchOnMount: true }
  );

  const userOrders = useMemo(
    () => handleOrders(ordersResponse?.data || [], userId),
    [ordersResponse?.data, userId]
  );

  const totalUserOrders = ordersResponse?.meta?.total;

  const userWithSubsResponse = useQuery(
    [QUERY_KEYS.GET_USER_WITH_SUBS_KEY, userId],
    () => usersServices.getUserWithSubs(userId),
    { enabled: router.isReady && userId && userId !== 'undefined' }
  );

  const userSubscriptionsResponse = useQuery(
    [QUERY_KEYS.GET_USER_SUBSCRIPTIONS_KEY, userId],
    () => usersServices.getUserSubscriptions(userId),
    { enabled: router.isReady && userId && userId !== 'undefined' }
  );

  const userResponse = useQuery(
    [QUERY_KEYS.GET_USER_KEY, userId],
    () => usersServices.getUser(userId),
    { enabled: router.isReady && userId && userId !== 'undefined' }
  );

  const userBookingsResponse = useQuery(
    [
      QUERY_KEYS.GET_USER_BOOKINGS_KEY,
      { userId, pageSize: bookingsPageSize, page },
    ],
    () =>
      usersServices.getUserBookings(
        userId,
        getCleanedUrlParams({
          page,
          pageSize: bookingsPageSize,
        })
      ),
    { enabled: router.isReady && userId && userId !== 'undefined' }
  );

  const userCreditScoreResponse = useQuery(
    [QUERY_KEYS.GET_USER_CREDIT_SCORE_KEY, userId],
    () => usersServices.getUserCreditScore(userId),
    { enabled: router.isReady && userId && userId !== 'undefined' }
  );
  const userSellRequestResponse = useQuery(
    [QUERY_KEYS.GET_USER_SELL_REQUEST_KEY, userId],
    () => boardsServices?.getBoardsFilters(getSellRequestsPayload(userId)),
    { enabled: router.isReady && userId && userId !== 'undefined' }
  );

  const userLeasingBuyoutsResponse = useQuery(
    [QUERY_KEYS.GET_USER_LEASING_BUYOUT_REQUEST_KEY, userId],
    () => boardsServices?.getBoardsFilters(getLeasingBuyoutsPayload(userId)),
    { enabled: router.isReady && userId && userId !== 'undefined' }
  );

  const userCommentsResponse = useQuery(
    [QUERY_KEYS.GET_USER_COMMENTS_KEY, userId],
    () => usersServices.getUserComments(userId),
    {
      enabled:
        router.isReady &&
        userId &&
        userId !== 'undefined' &&
        isHistoryTabComment,
    }
  );

  const phoneNumber = userResponse?.data?.data?.phoneNumber;

  const smsResponse = useQuery(
    [QUERY_KEYS.GET_USER_SMS_KEY, userId],
    () =>
      usersServices.getUserSMS({
        params: { page: 1, pageSize: 500, order: 'desc', orderBy: 'createdAt' },
        payload: {
          type: FILTER_RULE_ENUMS.AndRuleType,
          field: null,
          operator: null,
          value: [
            {
              field: 'sent_to',
              operator: FILTER_OPERATOR_ENUMS['='],
              type: FILTER_RULE_ENUMS.ComparableRuleType,
              value: phoneNumber,
            },
          ],
        },
      }),
    {
      enabled:
        router.isReady &&
        !!userId &&
        userId !== 'undefined' &&
        isHistoryTabSMS &&
        !!phoneNumber,
    }
  );

  const normalizedSMS = useMemo(
    () =>
      handleUserSMS({
        sms: smsResponse?.data?.data || [],
      }),
    [smsResponse?.data?.data]
  );

  const userComments = useMemo(() => {
    return handleUserComments(
      userCommentsResponse?.data?.data || [],
      loggedInUser,
      getAssigneeName
    );
  }, [userCommentsResponse?.data, loggedInUser, getAssigneeName]);

  const userHistoryQueryInfiniteQuery = useInfinitePagination({
    queryKey: QUERY_KEYS.GET_USER_HISTORY_CHANGES_KEY,
    params: {
      userId,
      pageSize: USER_HISTORY_CHANGES_DEFAULT_PAGE_SIZE,
    },
    enabled: Boolean(userId),
    initialPage: 1,
    queryFn: usersServices.getUserHistoryChanges,
  });

  const userHistoryQueryItemsHandled = useMemo(() => {
    return handleInfiniteQueryUserData(
      userHistoryQueryInfiniteQuery?.items,
      loggedInUser,
      handleChangelogItemFieldName,
      isSystemActor,
      getAssigneeName
    );
  }, [
    userHistoryQueryInfiniteQuery?.items,
    loggedInUser,
    handleChangelogItemFieldName,
    isSystemActor,
    getAssigneeName,
  ]);

  const userDeleteCommentMutation = useMutation(
    usersServices.deleteUserComment
  );

  const userHistoryQueryData = useMemo(() => {
    if (isHistoryTabComment) {
      return {
        ...userCommentsResponse,
        items: userComments,
      };
    }

    if (isHistoryTabSMS) {
      return {
        ...smsResponse,
        items: normalizedSMS,
      };
    }

    return {
      ...userHistoryQueryInfiniteQuery,
      items: userHistoryQueryItemsHandled,
    };
  }, [
    isHistoryTabComment,
    isHistoryTabSMS,
    userCommentsResponse,
    userHistoryQueryInfiniteQuery,
    userHistoryQueryItemsHandled,
    userComments,
    smsResponse,
    normalizedSMS,
  ]);

  const addCommentMutation = useMutation(usersServices.addUserComment);

  const handleUserAddComment = useCallback(
    (commentData) => {
      addCommentMutation.mutate(
        {
          entityId: userId,
          entityType: ENTITY_TYPES_COMMENTS.user,
          comment: commentData.content,
        },
        {
          onSuccess: () => {
            userCommentsResponse?.refetch?.();
            userHistoryQueryInfiniteQuery?.refetch?.();
          },
          onError: (error) => {
            showError(error);
          },
        }
      );
    },
    [
      addCommentMutation,
      userId,
      userCommentsResponse,
      userHistoryQueryInfiniteQuery,
    ]
  );

  const handleUserDeleteComment = useCallback(
    (commentItem) => {
      const commentId = commentItem?.id || commentItem?.resourceId;
      if (!commentId) return;

      userDeleteCommentMutation.mutate(commentId, {
        onSuccess: () => {
          userCommentsResponse?.refetch?.();
          userHistoryQueryInfiniteQuery?.refetch?.();
        },
        onError: (error) => {
          showError(error);
        },
      });
    },
    [
      userDeleteCommentMutation,
      userCommentsResponse,
      userHistoryQueryInfiniteQuery,
    ]
  );

  const userHistoryQuery = useMemo(() => {
    return {
      ...userHistoryQueryData,
      onAddComment: handleUserAddComment,
      onDeleteComment: handleUserDeleteComment,
    };
  }, [userHistoryQueryData, handleUserAddComment, handleUserDeleteComment]);

  const error =
    userWithSubsResponse.error ||
    userSubscriptionsResponse.error ||
    userResponse.error ||
    userBookingsResponse.error ||
    userCreditScoreResponse.error ||
    userSellRequestResponse.error ||
    userLeasingBuyoutsResponse.error;

  const loading =
    userWithSubsResponse.isLoading ||
    userSubscriptionsResponse.isLoading ||
    userResponse.isLoading ||
    userBookingsResponse.isLoading ||
    userCreditScoreResponse.isLoading ||
    userSellRequestResponse.isLoading ||
    userLeasingBuyoutsResponse.isLoading ||
    isUserOrdersLoading ||
    isServicePointsLoading;

  const refetch = () => {
    userResponse.refetch();
    userBookingsResponse.refetch();
    userSubscriptionsResponse.refetch();
    userCreditScoreResponse.refetch();
    userSellRequestResponse.refetch();
    userLeasingBuyoutsResponse.refetch();
  };

  useEffect(() => {
    if (error) {
      showError(error);
    }
  }, [error]);

  const details = handleUser(userResponse?.data?.data || {});
  const bookings = handleUserBookings(userBookingsResponse?.data?.data || []);

  const leasingBuyouts = handleUserLeasingBuyouts(
    userLeasingBuyoutsResponse?.data?.data || []
  );
  const creditScore = handleUserCreditScore(
    userCreditScoreResponse?.data?.data || {}
  );

  const userWithSubs = handleUserWithSubs(
    userWithSubsResponse?.data?.data || []
  );

  const subscriptions = handleUserSubscriptions(
    userSubscriptionsResponse?.data?.data || [],
    userId,
    servicePointsMap
  );

  return {
    details,
    totalUserOrders,
    userOrdersPage: page,
    userOrdersPageSize: b2bPageSize,
    loading,
    bookings,
    creditScore,
    leasingBuyouts,
    userId,
    userWithSubs,
    subscriptions,
    loggedInUser,
    userOrders,
    isUserOrdersLoading,
    userHistoryQuery,
    getUserOrders: refetchUserOrders,
    getUser: refetch,
    getUserBookings: userBookingsResponse.refetch,
    getUserWithSubs: userWithSubsResponse.refetch,
    getUserSubscriptions: userSubscriptionsResponse.refetch,
    getUserCreditScore: userCreditScoreResponse.refetch,
  };
};
